import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import moment from "moment";
import { ButtonGroup, Button, Dropdown } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroll-component";
import {
	FiPlus,
	FiMoreHorizontal,
	FiSliders,
	FiClock,
	FiUpload,
	FiDownload,
} from "react-icons/fi";
import { useAttendance } from "../../../hooks/attendance/AttendanceContext";
import useAttendanceExport from "../../../hooks/attendance/useAttendanceExport";
import { showModal, hideModal } from "actions/modalActions";
import { timesService } from "services";
import { createApprovalLock } from "utils/approvalInFlight";
import Loading from "components/Loading";
import TimesNavigationTabs from "../../Times/TimesNavigationTabs";
import { TIME_LIST_VIEWS } from "pages/AttendanceSupervisor";
import MobileAttendanceDateNav from "./MobileAttendanceDateNav";
import {
	MobileAttendanceIndividualCard,
	MobileAttendanceDayCard,
} from "./MobileAttendanceCard";
import MobileAttendanceFiltersSheet from "./MobileAttendanceFiltersSheet";

const isValidIso = (value) =>
	value && moment(value, moment.ISO_8601, true).isValid();

/**
 * Agrupa ítems (vista Individual) por día para mostrar secciones "Mar 16 Feb 2026".
 */
const groupItemsByDay = (items) => {
	const groups = new Map();
	items.forEach((item) => {
		const dayKey = item.day
			? moment(item.day).format("YYYY-MM-DD")
			: moment(item.start).format("YYYY-MM-DD");
		if (!groups.has(dayKey)) {
			groups.set(dayKey, []);
		}
		groups.get(dayKey).push(item);
	});
	return Array.from(groups.entries())
		.sort((a, b) => (a[0] < b[0] ? 1 : -1))
		.map(([day, records]) => ({ day, records }));
};

/**
 * Agrupa ítems (vista Por día) por empleado+día para la card extendida.
 */
const groupItemsByEmployeeDay = (items) => {
	const groupMap = new Map();
	items.forEach((item) => {
		const employeeId = item.employee?.id || item.employee_id || "unknown";
		const dayKey = item.day
			? moment(item.day).format("YYYY-MM-DD")
			: moment(item.start).format("YYYY-MM-DD");
		const key = `${employeeId}-${dayKey}`;
		if (!groupMap.has(key)) {
			groupMap.set(key, {
				id: key,
				employeeId,
				employee: item.employee,
				day: dayKey,
				records: [],
				totalSeconds: 0,
			});
		}
		const group = groupMap.get(key);
		group.records.push(item);
		if (item.start && item.end) {
			group.totalSeconds += moment(item.end).diff(moment(item.start), "seconds");
		}
	});

	const groups = Array.from(groupMap.values()).sort((a, b) =>
		b.day.localeCompare(a.day)
	);

	const byDay = new Map();
	groups.forEach((group) => {
		if (!byDay.has(group.day)) byDay.set(group.day, []);
		byDay.get(group.day).push(group);
	});

	return Array.from(byDay.entries()).map(([day, records]) => ({
		day,
		records,
	}));
};

const formatSectionDate = (day) => {
	const m = moment(day);
	const weekday = m.format("ddd").replace(/\./g, "");
	const cap = weekday.charAt(0).toUpperCase() + weekday.slice(1);
	return `${cap} ${m.format("D MMM YYYY")}`;
};

const MobileAttendanceView = ({
	timeListView: timeListViewProp,
	onChangeTimeListView,
}) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const user = useSelector((state) => state.authReducer.user);
	const {
		items,
		loading,
		loadingMore,
		hasMore,
		fetchMoreData,
		filters,
		setItems,
	} = useAttendance();
	const { exportToExcel } = useAttendanceExport();

	const [internalTimeListView, setInternalTimeListView] = useState(
		TIME_LIST_VIEWS.list
	);
	const timeListView = timeListViewProp ?? internalTimeListView;
	const setTimeListView = onChangeTimeListView || setInternalTimeListView;
	const [filtersOpen, setFiltersOpen] = useState(false);
	const [summary, setSummary] = useState(null);
	const [isApprovingAll, setIsApprovingAll] = useState(false);
	const approvalLockRef = useRef(createApprovalLock());

	useEffect(() => {
		timesService
			.getSummaryToday()
			.then((res) => setSummary(res))
			.catch(() => setSummary(null));
	}, []);

	const handleSwitchToggle = useCallback(
		(event, time) => {
			const isApproved = event.target.checked;
			const key = String(time.id);
			if (!approvalLockRef.current.tryBegin(key)) {
				return;
			}

			if (!time.start || !time.end) {
				approvalLockRef.current.end(key);
				dispatch(
					showModal({
						modalProps: {
							open: true,
							style: { width: "400px" },
							title: t("common.error"),
							message: t("attendance.approveNeedsStartEnd"),
							closeModal: () => dispatch(hideModal()),
						},
						modalType: "alert",
					})
				);
				return;
			}

			setItems((prev) =>
				prev.map((item) =>
					item.id === time.id ? { ...item, approved: isApproved } : item
				)
			);

			timesService
				.approve([{ id: time.id, approved: isApproved }])
				.catch((error) => {
					console.error(
						`Error al ${isApproved ? "aprobar" : "desaprobar"}:`,
						error
					);
					setItems((prev) =>
						prev.map((item) =>
							item.id === time.id
								? { ...item, approved: !isApproved }
								: item
						)
					);
				})
				.finally(() => {
					approvalLockRef.current.end(key);
				});
		},
		[dispatch, setItems, t]
	);

	// En mobile aprobamos por registro: cualquier fichaje con inicio + fin válidos
	// y aún no aprobado es candidato. No exigimos que todo el día del empleado
	// esté cerrado (eso descartaba registros correctos si había otro abierto
	// en el mismo día, que es la queja del usuario).
	const isApprovableRecord = useCallback(
		(item) =>
			!item.approved && isValidIso(item.start) && isValidIso(item.end),
		[]
	);

	const approvableCount = useMemo(() => {
		if (!items || items.length === 0) return 0;
		return items.filter(isApprovableRecord).length;
	}, [items, isApprovableRecord]);

	const handleApproveAll = useCallback(async () => {
		if (!approvalLockRef.current.tryBegin("period")) return;
		if (isApprovingAll) {
			approvalLockRef.current.end("period");
			return;
		}
		if (approvableCount === 0) {
			approvalLockRef.current.end("period");
			dispatch(
				showModal({
					modalProps: {
						open: true,
						title: t("common.info"),
						style: { width: "400px" },
						message: t("attendance.noPendingApprove"),
						closeModal: () => dispatch(hideModal()),
					},
					modalType: "alert",
				})
			);
			return;
		}

		const approvalList = items
			.filter(isApprovableRecord)
			.map((item) => ({ id: item.id, approved: true }));

		setIsApprovingAll(true);
		try {
			const response = await timesService.approve(approvalList);
			if (response && response.ok) {
				setItems((prev) =>
					prev.map((item) =>
						approvalList.some((a) => a.id === item.id)
							? { ...item, approved: true }
							: item
					)
				);
				dispatch(
					showModal({
						modalProps: {
							open: true,
							title: t("common.success"),
							style: { width: "400px" },
							message: t("attendance.approveSuccess"),
							closeModal: () => dispatch(hideModal()),
						},
						modalType: "alert",
					})
				);
			} else {
				throw new Error(
					response?.errors?.[0]?.description ||
						t("attendance.approveError")
				);
			}
		} catch (err) {
			console.error("approve all error:", err);
			dispatch(
				showModal({
					modalProps: {
						open: true,
						title: t("common.error"),
						style: { width: "400px" },
						message: err.message || t("attendance.approveError"),
						closeModal: () => dispatch(hideModal()),
					},
					modalType: "alert",
				})
			);
		} finally {
			approvalLockRef.current.end("period");
			setIsApprovingAll(false);
		}
	}, [approvableCount, items, dispatch, setItems, isApprovingAll, isApprovableRecord, t]);

	const groupedList = useMemo(() => {
		if (!items || items.length === 0) return [];
		return timeListView === TIME_LIST_VIEWS.employee_day
			? groupItemsByEmployeeDay(items)
			: groupItemsByDay(items);
	}, [items, timeListView]);

	const openTimesCount = summary?.total_open_time_registrations ?? null;

	const handleExport = () => {
		exportToExcel({
			employeeIds: filters.employeeIds,
			startDate: moment(filters.dateRange.start).format("YYYY-MM-DD"),
			endDate: moment(filters.dateRange.end).format("YYYY-MM-DD"),
			groupId: filters.groupId,
		});
	};

	const handleOpenNotEnded = () => {
		filters.toggleFilter("not_ended", "1");
	};

	return (
		<div className="mobile-attendance-view">
			<div className="mobile-attendance-view__toolbar">
				<ButtonGroup className="mobile-attendance-view__toggle">
					<Button
						variant={
							timeListView === TIME_LIST_VIEWS.list ? "primary" : "default"
						}
						onClick={() => setTimeListView(TIME_LIST_VIEWS.list)}
					>
						{t("attendance.viewIndividual")}
					</Button>
					<Button
						variant={
							timeListView === TIME_LIST_VIEWS.employee_day
								? "primary"
								: "default"
						}
						onClick={() => setTimeListView(TIME_LIST_VIEWS.employee_day)}
					>
						{t("attendance.viewByDay")}
					</Button>
				</ButtonGroup>

				<Link
					to={`/times/${user?.id}/employee`}
					title={t("times.registerTimes")}
					className="mobile-attendance-view__add"
				>
					<FiPlus size={20} />
				</Link>

				<Dropdown align="end">
					<Dropdown.Toggle
						as="button"
						className="mobile-attendance-view__more"
						aria-label={t("attendance.moreOptions")}
					>
						<FiMoreHorizontal size={20} />
					</Dropdown.Toggle>
					<Dropdown.Menu>
						<Dropdown.Item as={Link} to="/time-registrations/import">
							<FiDownload className="mr5" /> {t("times.dropdown.importTimes")}
						</Dropdown.Item>
						<Dropdown.Item as="button" onClick={handleExport}>
							<FiUpload className="mr5" /> {t("times.dropdown.exportExcel")}
						</Dropdown.Item>
					</Dropdown.Menu>
				</Dropdown>
			</div>

			<TimesNavigationTabs />

			<div className="mobile-attendance-view__date-wrapper">
				<MobileAttendanceDateNav
					startDate={filters.dateRange.start}
					endDate={filters.dateRange.end}
					activeDatePreset={filters.activeDatePreset}
					onDatesChange={filters.handleDateChange}
					onPresetChange={filters.setDatePreset}
				/>
			</div>

			{openTimesCount > 0 && (
				<div
					className="mobile-attendance-view__banner"
					role="button"
					tabIndex={0}
					onClick={handleOpenNotEnded}
					onKeyDown={(e) => {
						if (e.key === "Enter" || e.key === " ") {
							e.preventDefault();
							handleOpenNotEnded();
						}
					}}
				>
					<span className="mobile-attendance-view__banner-icon">
						<FiClock size={16} />
					</span>
					<span className="mobile-attendance-view__banner-text">
						{t("attendance.openTimesCount", { count: openTimesCount })}
					</span>
					<span className="mobile-attendance-view__banner-action">
						{t("attendance.viewStampings")}
					</span>
				</div>
			)}

			<div className="mobile-attendance-view__body">
				{loading && items.length === 0 ? (
					<div className="mobile-attendance-view__loading">
						<Loading />
					</div>
				) : items.length === 0 ? (
					<div className="mobile-attendance-view__empty">
						{t("attendance.noRecords")}
					</div>
				) : (
					<InfiniteScroll
						dataLength={items.length}
						next={fetchMoreData}
						hasMore={hasMore}
						scrollThreshold={0.9}
						loader={
							loadingMore ? (
								<div className="mobile-attendance-view__load-more">
									{t("attendance.loadingMore")}
								</div>
							) : null
						}
						endMessage={
							<div className="mobile-attendance-view__end-message">
								{t("attendance.noMoreInPeriod")}
							</div>
						}
						style={{ overflow: "visible" }}
					>
						{groupedList.map(({ day, records }) => (
							<div key={day} className="mobile-attendance-view__section">
								<div className="mobile-attendance-view__section-title">
									{formatSectionDate(day)}
								</div>
								<div className="mobile-attendance-view__section-list">
									{timeListView === TIME_LIST_VIEWS.employee_day
										? records.map((group) => (
												<MobileAttendanceDayCard
													key={group.id}
													group={group}
													onToggleApproved={handleSwitchToggle}
												/>
										  ))
										: records.map((record) => (
												<MobileAttendanceIndividualCard
													key={record.id}
													item={record}
													onToggleApproved={handleSwitchToggle}
												/>
										  ))}
								</div>
							</div>
						))}
					</InfiniteScroll>
				)}
			</div>

			<div className="mobile-attendance-view__bottom-bar">
				<button
					type="button"
					className="mobile-attendance-view__filters-btn"
					onClick={() => setFiltersOpen(true)}
					aria-label={t("attendance.filters")}
				>
					<FiSliders size={20} />
				</button>
				<button
					type="button"
					className="mobile-attendance-view__approve-btn"
					onClick={handleApproveAll}
					disabled={isApprovingAll || approvableCount === 0}
				>
					{isApprovingAll
						? t("attendance.approvingAll")
						: t("attendance.approveAllCount", { count: approvableCount })}
				</button>
			</div>

			<MobileAttendanceFiltersSheet
				open={filtersOpen}
				onClose={() => setFiltersOpen(false)}
				timeListView={timeListView}
			/>
		</div>
	);
};

export default MobileAttendanceView;
